import { useState, useEffect, useRef } from 'preact/hooks';
import { applyCurrencyToDocument, getCurrencySettings, getCurrentCurrency, setCurrencySettings, setSelectedCurrency } from '../utils/currency';
import { store } from '../store';

function getCurFromUrl(): string {
  if (typeof window === 'undefined') return getCurrentCurrency();
  const m = window.location.search.match(/[?&]cur=([^&]+)/i);
  return m ? m[1].toUpperCase() : getCurrentCurrency();
}

function setCurInUrl(cur: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('cur', cur);
  window.history.replaceState(window.history.state, '', url.toString());
}

export function CurrencyWidget() {
  const [currencies, setCurrencies] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState(getCurFromUrl());
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const syncFromSettings = () => {
      const fromStore = store.getSettings();
      const settings = fromStore?.CURRENCY_SYMBOLS ? fromStore : getCurrencySettings();
      if (settings.CURRENCY_SYMBOLS) {
        setCurrencySettings(settings);
        setCurrencies(settings.CURRENCY_SYMBOLS);
      }
      setCurrent(getCurFromUrl());
    };
    syncFromSettings();
    return store.subscribe(syncFromSettings);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current || typeof window === 'undefined') return;
    const raf = window.requestAnimationFrame(() => {
      const menu = dropdownRef.current?.querySelector('.dropdown-menu.show') as HTMLElement | null;
      if (!menu) return;
      const navScroller = menu.closest('.sl-nav') as HTMLElement | null;
      if (!navScroller) return;
      const navRect = navScroller.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const pad = 12;
      const bottomOverflow = menuRect.bottom - navRect.bottom + pad;
      if (bottomOverflow > 0) navScroller.scrollTop += bottomOverflow;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isOpen]);

  const entries = Object.entries(currencies);
  if (entries.length === 0) {
    return (
      <span class="nav-link d-flex align-items-center notranslate">{current}</span>
    );
  }
  const currentSymbol = currencies[current] || current;

  return (
    <div class="dropdown notranslate" ref={dropdownRef}>
      <a
        class="nav-link dropdown-toggle d-flex align-items-center"
        href="#"
        role="button"
        aria-expanded={isOpen}
        style={{ color: 'var(--sw-header-links, var(--sw-primary))' }}
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        <span
          class="sw-currency-icon"
          aria-hidden="true"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', marginRight: '0.35rem', flex: '0 0 20px', fontSize: '19px', fontWeight: 700, lineHeight: 1 }}
        >
          {currentSymbol}
        </span>
        {current}
      </a>
      <ul
        class={`dropdown-menu ${isOpen ? 'show' : ''}`}
      >
        {entries.map(([code, symbol]) => (
          <li key={code}>
            <a
              class={`dropdown-item ${code === current ? 'active' : ''}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedCurrency(code);
                store.setSettings({ ...store.getSettings(), CURRENCY: code });
                setCurrent(code);
                setCurInUrl(code);
                applyCurrencyToDocument();
                setIsOpen(false);
                window.dispatchEvent(new CustomEvent('shop-widget:currency-change', { detail: { currency: code } }));
              }}
            >
              {symbol} {code}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
